import { useState, useEffect } from "react";
import "./QuestionSettings.css";
import { useDispatch, useSelector } from "react-redux";
import { 
    selectEditingForm,
    selectEditingQuestionId,
    selectUIStates,
    setEditingForm,
    setUIStates
} from "../../redux/features/appData";
import { Question, QuestionType } from "effno-core";
import { QuestionSettingsShort } from "./QuestionSettingsShort";
import toast from "react-hot-toast";
import { toastFail, toastSuccess } from "../../config";
import { QuestionSettingsLong } from "./QuestionSettingsLong";
import { QuestionSettingsEmail } from "./QuestionSettingsEmail";
import { QuestionSettingsWebsite } from "./QuestionSettingsWebsite";
import { QuestionSettingsMultiple } from "./QuestionSettingsMultiple";
import { QuestionSettingsEnd } from "./QuestionSettingsEnd";
import { QuestionWorkflow } from "../workflow/QuestionWorkflow";
import { QuestionSettingsPhone } from "./QuestionSettingsPhone";
import { QuestionSettingsNumber } from "./QuestionSettingsNumber";
import { QuestionSettingsContact } from "./QuestionSettingsContact";
import { QuestionSettingsNPS } from "./QuestionSettingsNPS";
import { QuestionSettingsScore } from "./QuestionSettingsScore";
import { QuestionSettingsRating } from "./QuestionSettingsRating";
import { QuestionComments } from "../question-comments/QuestionComments";
import { QuestionSettingsDate } from "./QuestionSettingsDate";
import { QuestionSettingsSection } from "./QuestionSettingsSection";
import { apiFormHasEdits } from "../../services/FormService";
import { getUserNotificationSettings } from "../../services/NotificationService";
import { Share2, SlidersHorizontal, Workflow } from "lucide-react";

export const QUESTION_SETTINGS_TABS = {
    SETTINGS: 0,
    WORKFLOW: 1,
    // DESIGN: 2,
    // COMMENTS: 3,
}

type QuestionSettingsProps = {
}

export const QuestionSettings = ({} : QuestionSettingsProps) => {

    const dispatch = useDispatch();

    const editingForm = useSelector(selectEditingForm);
    const editingQuestionId = useSelector(selectEditingQuestionId);
    const uiStates = useSelector(selectUIStates);

    const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);

    useEffect(() => {
        if (editingQuestionId && editingForm) {
            const question = editingForm.questions.find((question : Question) => question.id === editingQuestionId);
            if (question) {
                setEditingQuestion(question);

                // if its an end there is no workflow tab so make sure we're on settings
                if (question.type === QuestionType.END) {
                    dispatch(setUIStates({...uiStates, questionSettingsTab: QUESTION_SETTINGS_TABS.SETTINGS}));
                }
            }
        }
    }, [editingForm, editingQuestionId]);

    const updateQuestion = async(question: Question) => {
        if (!editingForm) {
            return;
        }
        
        // update redux
        const index = editingForm.questions.findIndex((q: Question) =>  q.id === question.id);

        if (index === -1) {
            toast("Can't find question id", toastFail);
            return;
        }

        const newQuestions = [...editingForm.questions];
        newQuestions[index] = question;

        await apiFormHasEdits(editingForm.id);

        dispatch(setEditingForm({...editingForm, hasEdits: true, questions: newQuestions}));

        console.log("Question updated");
    }

    const parentComments = editingQuestion ? 
    editingQuestion?.comments.filter((comment) => !comment.parentQuestionCommentId) : [];
    // console.log("[QuestionSettings] editingQuestion: ", editingQuestion); 

    return (        
        <div 
            className="question-settings"
            data-testid={"question-settings#" + editingQuestionId}
        >
           
           {editingQuestion && 
                <div className="question-settings-tabs">
                    <div 
                        className={"question-settings-tab" + (uiStates.questionSettingsTab === QUESTION_SETTINGS_TABS.SETTINGS ? " question-settings-tab-active" : "")}
                        onClick={() => dispatch(setUIStates({...uiStates, questionSettingsTab: QUESTION_SETTINGS_TABS.SETTINGS}))}
                        data-testid="question-settings-tab-settings"
                    >
                        <SlidersHorizontal size={10}/>&nbsp;Settings
                    </div>
                    
                    <div 
                        className={"question-settings-tab" + (uiStates.questionSettingsTab === QUESTION_SETTINGS_TABS.WORKFLOW ? " question-settings-tab-active" : "")}
                        onClick={() => dispatch(setUIStates({...uiStates, questionSettingsTab: QUESTION_SETTINGS_TABS.WORKFLOW}))}
                        data-testid="question-settings-tab-workflow"
                    >
                        <Share2 size={10}/>&nbsp;Workflow
                    </div>
                </div>
            }
            
                
            {uiStates.questionSettingsTab === QUESTION_SETTINGS_TABS.SETTINGS && (
                () => {
                    switch(editingQuestion?.type) {
                        case QuestionType.SHORT:
                            return (
                                <QuestionSettingsShort onChange={updateQuestion} question={editingQuestion} />
                            )
                        case QuestionType.LONG:
                            return (
                                <QuestionSettingsLong onChange={updateQuestion} question={editingQuestion} />
                            )
                        case QuestionType.MULTIPLE:
                            return (
                                <QuestionSettingsMultiple onChange={updateQuestion} question={editingQuestion} />
                            )      
                        case QuestionType.DATE:
                            return (
                                <QuestionSettingsDate onChange={updateQuestion} question={editingQuestion} />
                            )
                        case QuestionType.EMAIL:
                            return (
                                <QuestionSettingsEmail onChange={updateQuestion} question={editingQuestion} />
                            )  
                        case QuestionType.FILE_UPLOAD:
                            return (
                                <QuestionSettingsShort onChange={updateQuestion} question={editingQuestion} />
                            )
                        case QuestionType.WEBSITE:
                            return (
                                <QuestionSettingsWebsite onChange={updateQuestion} question={editingQuestion} />
                            )
                        case QuestionType.NUMBER:
                            return (
                                <QuestionSettingsNumber onChange={updateQuestion} question={editingQuestion} />
                            )
                        case QuestionType.PHONE:
                            return (
                                <QuestionSettingsPhone onChange={updateQuestion} question={editingQuestion} />
                            )
                        case QuestionType.CONTACT:
                            return (
                                <QuestionSettingsContact onChange={updateQuestion} question={editingQuestion} />
                            )
                        case QuestionType.NPS:
                            return (
                                <QuestionSettingsNPS onChange={updateQuestion} question={editingQuestion} />
                            )
                        case QuestionType.SCORE:
                            return (
                                <QuestionSettingsScore onChange={updateQuestion} question={editingQuestion} />
                            )
                        case QuestionType.RATING:
                            return (
                                <QuestionSettingsRating onChange={updateQuestion} question={editingQuestion} />
                            )
                        case QuestionType.SECTION:
                            return (
                                <QuestionSettingsSection onChange={updateQuestion} question={editingQuestion} />
                            )
                        case QuestionType.END:
                            return (
                                <QuestionSettingsEnd onChange={updateQuestion} question={editingQuestion} />
                            )
                        default:
                            return (
                                <div>
                                    
                                </div>
                            );
                    }
                }
                )()
            }

            {editingQuestion && uiStates.questionSettingsTab === QUESTION_SETTINGS_TABS.WORKFLOW && 
                <QuestionWorkflow onChange={updateQuestion} question={editingQuestion} />
            }

            {/* // {editingQuestion && uiStates.questionSettingsTab === QUESTION_SETTINGS_TABS.DESIGN && 
            //     <QuestionDesign />
            // } */}

            {/* // {editingQuestion && uiStates.questionSettingsTab === QUESTION_SETTINGS_TABS.COMMENTS && 
            //     <QuestionComments comments={editingQuestion.comments}/>
            // } */}
            
        </div>
    )
}